import {Routes as DomRoutes, Route as DomRoute, useLocation} from 'react-router-dom';
import React from 'react';
import {connect} from 'react-redux';
import CardPage from '../pages/CardPage';
import CardsPage from '../pages/CardsPage';
import OverviewPage from '../pages/OverviewPage';
import OverviewUserPage from '../pages/OverviewUserPage';
import TeamUserPage from '../pages/TeamUserPage';
import SettingsPage from '../pages/SettingsPage';
import ProfileSettingsPage from '../pages/ProfileSettingsPage';
import SignUpPage from '../pages/SignUpPage';
import LoginPage from '../pages/LoginPage';
import ForgotPinPage from '../pages/ForgotPinPage';
import TransactionsPage from '../pages/TransactionsPage';
import SubscriptionsPage from '../pages/SubscriptionsPage';
import SubscriptionPage from '../pages/SubscriptionPage';
import CompanyInvitePage from '../pages/CompanyInvitePage';
import EmailVerificationPage from '../pages/EmailVerificationPage';
import NotFoundPage from '../pages/NotFoundPage';
import VerifySuccessPage from '../pages/VerifySuccessPage';
import UserInvitePage from '../pages/UserInvitePage';
import OAuthCallbackPage from '../pages/OAuthCallbackPage';
import QuickGoogleConnectPage from '../pages/QuickGoogleConnectPage';
import PublicSubscriptionUpdatePage from '../pages/PublicSubscriptionUpdatePage';
import ExpensesPage from '../pages/ExpensesPage';
import InvoicePage from '../pages/InvoicePage';
import InvoicesPage from '../pages/InvoicesPage';
import NetSuiteBillsPage from '../pages/NetSuiteBillsPage';
import NetSuiteJournalEntriesPage from '../pages/NetSuiteJournalEntriesPage';
import NetSuiteVendorsPage from '../pages/NetSuiteVendorsPage';
import SupportPage from '../pages/SupportPage';
import VendorsPage from '../pages/VendorsPage';
import BudgetsActivePage from '../pages/BudgetsActivePage';
import BudgetsRequestsPage from '../pages/BudgetsRequestsPage';
import BudgetActiveDetailsPage from '../pages/BudgetActiveDetailsPage';
import BudgetRequestDetailsPage from '../pages/BudgetRequestDetailsPage';
import routes from './routes.json';
import {availableModulesConstants, companyFeaturesConstants} from '../constants';
import {StyledSpin} from '../app/StyledApp';
import {locationHelpers, systemHelpers} from '../utils/helpers';
import {useFeatureAvailable, useIsTreezorEnabled} from '../hooks';

const {TRANSACTIONS, SUBSCRIPTIONS} = availableModulesConstants;

const isAvailableSubscriptionsModule = systemHelpers.checkIfAvailableFeature(SUBSCRIPTIONS);
const isAvailableTransactionsModule = systemHelpers.checkIfAvailableFeature(TRANSACTIONS);
const isRequiredCheckPath = isAvailableSubscriptionsModule || isAvailableTransactionsModule;

const bankingRoutes = ['cardDetails', 'cardsList', 'invoiceDetails', 'invoicesList', 'subscriptionsList',
  'subscriptionDetails', 'transactionsList', 'vendorsList', 'netSuiteJournalEntriesList',
  'netSuiteVendorBills', 'netSuiteVendors'
].map(key => routes[key]);

const Routes = ({
  availableModules,
  companyState,
  isAdmin,
  isAuthenticated,
  isKnownEmployee,
}) => {
  const location = useLocation();

  const isAccountingAvailable = useFeatureAvailable(companyFeaturesConstants.ACCOUNTING_MODULE);
  const isBudgetsAvailable = useFeatureAvailable(companyFeaturesConstants.BUDGETS_PO_AVAILABLE);

  const isTreezorEnabled = useIsTreezorEnabled();

  const Route = (key, element) => {
    if (routes.hasOwnProperty(key)) {
      return (
        <DomRoute
          key={key}
          exact
          path={routes[key]}
          element={element}
        />
      )
    } else {
      return null;
    }
  };

  const getAdminRoutes = () => {
    return [
      Route('settings', <SettingsPage />),
      Route('teamUserDetails', <TeamUserPage />),
    ];
  }

  const getMainRoutes = () => {
    const isBankingRoute = locationHelpers.isActivePathInList(location.pathname, bankingRoutes);
    return [
      <DomRoute
        element={<CompanyInvitePage isAuth={isAuthenticated} />}
        key='invite'
        exact
        path={routes.invite}
      />,
      Route('verification', <EmailVerificationPage />),
      Route('verifyIdentity', <VerifySuccessPage />),
      Route('userInvite', <UserInvitePage />),
      Route('oauthCallback', <OAuthCallbackPage />),
      Route('publicSubscriptionUpdate', <PublicSubscriptionUpdatePage />),
      Route('quickGoogleConnect', <QuickGoogleConnectPage />),
      <DomRoute
        // Display loaded while company state is defined if the current route is banking
        element={
          isRequiredCheckPath && isBankingRoute
            ? (companyState && availableModules?.loaded === true) || !isAuthenticated
              ? <NotFoundPage /> : (
                <StyledSpin
                  spinning={true}
                  wrapperClassName='full-width-spin'
                />
              )
            : <NotFoundPage />
        }
        path='*'
        key='not-found'
      />
    ];
  }

  const adminRoutes = getAdminRoutes();

  const mainRoutes = getMainRoutes();

  if (!isAuthenticated) {
    return (
      <DomRoutes>
        {Route('signUp', <SignUpPage />)}
        {Route('login', <LoginPage />)}
        {Route('forgotPin', <ForgotPinPage />)}
        {mainRoutes}
      </DomRoutes>
    )
  }

  const getAppRoutes = () => {
    let appRoutes = [
      Route('overview', isKnownEmployee ? isAdmin ? <OverviewPage /> : <OverviewUserPage /> : null),
      Route('expensesList', <ExpensesPage />),
      Route('subscriptionDetails', <SubscriptionPage />),
      Route('profile', <ProfileSettingsPage />),
      Route('support', <SupportPage />),
    ];

    if (isTreezorEnabled) {
      appRoutes = [ ...appRoutes, Route('cardDetails', <CardPage />) ];

      if (isAdmin) {
        appRoutes = [
          ...appRoutes,
          Route('cardsList', <CardsPage />),
          Route('transactionsList', <TransactionsPage />)
        ];
      }
    }

    if (isBudgetsAvailable) {
      appRoutes = [
        ...appRoutes,
        Route('budgetsActive', <BudgetsActivePage />),
        Route('budgetsRequests', <BudgetsRequestsPage />),
        Route('budgetActiveDetails', <BudgetActiveDetailsPage />),
        Route('budgetRequestsDetails', <BudgetRequestDetailsPage />),
      ];
    }

    if (isAdmin) {
      appRoutes = [
        ...appRoutes,
        ...adminRoutes,
        Route('subscriptionsList', <SubscriptionsPage />),
        Route('invoiceDetails', <InvoicePage />),
        Route('invoicesList', <InvoicesPage />),
        Route('vendorsList', <VendorsPage />),
      ];
      if (isAccountingAvailable) {
        appRoutes = [
          ...appRoutes,
          Route('netSuiteVendorBills', <NetSuiteBillsPage />),
          Route('netSuiteVendors', <NetSuiteVendorsPage />),
          Route('netSuiteJournalEntriesList', <NetSuiteJournalEntriesPage />),
        ];
      }
    }

    appRoutes = [...appRoutes, ...mainRoutes];

    return appRoutes;
  }

  const appRoutes = getAppRoutes();

  return (
    <DomRoutes location={location} key={location.pathname}>
      {appRoutes}
    </DomRoutes>
  );
}

const mapStateToProps = state => {
  const {banking, user} = state;
  const {isAdmin, isKnownEmployee} = user;

  return {
    availableModules: state?.main?.availableModules,
    isAdmin,
    isKnownEmployee,
    isAuthenticated: user.isAuth,
    companyState: banking?.companyState
  }
}

export default connect(mapStateToProps, null)(Routes);
